// ─── Template Picker ──────────────────────────────────────────
// Step 1 of invitation creation: choose a template.
// Shows live mini phone previews of each template's hero.

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { elegantWeddingV1 } from '../lib/templates/elegant-wedding-v1';
import { noirVinylV1 } from '../lib/templates/noir-vinyl-v1';
import { timelessEleganceV1 } from '../lib/templates/timeless-elegance-v1';
import { modernMinimalV1 } from '../lib/templates/modern-minimal-v1';
import { editorialRomanceV1 } from '../lib/templates/editorial-romance-v1';
import { eveningLuxeV1 } from '../lib/templates/evening-luxe-v1';
import { memoryCollageV1 } from '../lib/templates/memory-collage-v1';
import { oceanEditorialCollageV1 } from '../lib/templates/ocean-editorial-collage-v1';
import { universalStarterV1 } from '../lib/templates/universal-starter-v1';
import { freeformStudioV1 } from '../lib/templates/freeform-studio-v1';
import { palettes } from '../lib/tokens/palettes';
import { resolveTheme } from '../lib/tokens/themes';
import HeroSection from '../components/sections/hero/HeroSection';
import CollageCanvas from '../components/collage/CollageCanvas';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const NEW_TEMPLATES = [freeformStudioV1, universalStarterV1, oceanEditorialCollageV1, memoryCollageV1, elegantWeddingV1, noirVinylV1, timelessEleganceV1, modernMinimalV1, editorialRomanceV1, eveningLuxeV1];

// Phone frame dimensions (the "viewport" the hero renders into)
const PHONE_W = 375;
const PHONE_H = 680;

function MiniPhonePreview({ template }) {
  const theme = useMemo(() => resolveTheme(template.theme), [template.theme]);
  const heroSection = template.sections.find(s => s.type === 'hero');
  const firstCollage = template.sections.find(s => s.type === 'collage');

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Phone bezel */}
      <div
        className="relative rounded-[2rem] overflow-hidden shadow-xl border-[3px] border-gray-300/40 dark:border-gray-600/40"
        style={{ width: 180, height: 320 }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 w-16 h-4 rounded-b-xl bg-gray-300/30 dark:bg-gray-600/30" />

        {/* Scaled-down preview */}
        <div
          className="origin-top-left pointer-events-none"
          style={{
            width: PHONE_W,
            height: PHONE_H,
            transform: `scale(${180 / PHONE_W})`,
            overflow: 'hidden',
            backgroundColor: theme.bg,
            fontFamily: theme.bodyFont,
          }}
        >
          {template.isStudio ? (
            /* Studio freeform preview */
            <div className="w-full h-full flex flex-col items-center justify-center bg-white relative">
              {/* Grid lines to suggest a canvas */}
              <div className="absolute inset-0 opacity-[0.06]" style={{
                backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }} />
              {/* Simulated elements */}
              <div className="absolute" style={{ top: '8%', left: '10%', width: '55%', height: '30%', borderRadius: 8, background: 'linear-gradient(135deg, #e8e4df, #d4cfc8)', opacity: 0.7 }} />
              <div className="absolute" style={{ top: '12%', right: '8%', width: '28%', height: '18%', borderRadius: 6, background: 'linear-gradient(135deg, #d0c9bf, #c4bdb3)', opacity: 0.5 }} />
              <div className="absolute flex flex-col gap-1.5" style={{ top: '44%', left: '8%', width: '84%' }}>
                <div className="h-4 rounded bg-gray-800/80 w-3/4" />
                <div className="h-2.5 rounded bg-gray-500/40 w-full" />
                <div className="h-2.5 rounded bg-gray-500/30 w-2/3" />
              </div>
              <div className="absolute" style={{ bottom: '18%', left: '15%', width: '70%', height: '22%', borderRadius: 8, background: 'linear-gradient(135deg, #e2ddd7, #d8d2cb)', opacity: 0.6 }} />
              {/* Label */}
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-[11px] font-semibold text-gray-700 tracking-wide">STUDIO</p>
                <p className="text-[9px] text-gray-400">Freeform canvas</p>
              </div>
            </div>
          ) : heroSection ? (
            <HeroSection
              section={heroSection}
              data={heroSection.data}
              theme={theme}
              settings={{}}
            />
          ) : firstCollage ? (
            firstCollage.elements?.length > 0 ? (
              <CollageCanvas
                section={firstCollage}
                data={firstCollage.data || {}}
                theme={theme}
                settings={{}}
              />
            ) : (
              /* Blank canvas placeholder */
              <div className="w-full h-full flex flex-col items-center justify-center" style={{ backgroundColor: firstCollage.background?.color || theme.bg }}>
                <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center mb-4">
                  <span className="text-2xl text-gray-300">+</span>
                </div>
                <p className="text-xs font-medium text-gray-400 text-center px-8">Start from scratch</p>
                <p className="text-[10px] text-gray-300 mt-1">Drag, drop, style</p>
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function TemplatePicker() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selecting, setSelecting] = useState(null);
  const [curated, setCurated] = useState([]);
  const [loadingCurated, setLoadingCurated] = useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurated(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setCurated([]);
      } finally {
        setLoadingCurated(false);
      }
    };
    load();
  }, [token]);

  const handleSelectCurated = async (tmpl) => {
    setSelecting(tmpl.id);
    try {
      await axios.post(
        `${API}/api/events/${eventId}/apply-template/${tmpl.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${tmpl.name}" applied`);
      navigate(`/events/${eventId}/invitation-editor`);
    } catch (err) {
      toast.error('Failed to apply template');
    } finally {
      setSelecting(null);
    }
  };

  const handleSelect = async (template) => {
    setSelecting(template.templateId);
    try {
      await axios.put(
        `${API}/api/events/${eventId}/rsvp-page`,
        {
          engine: 'v2',
          v2_template_id: template.templateId,
          v2_content: {},
          v2_theme_overrides: {},
          v2_settings_overrides: {},
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Template "${template.name}" selected`);
      navigate(`/events/${eventId}/invitation-editor`);
    } catch (err) {
      if (err.response?.status === 404) {
        try {
          await axios.post(
            `${API}/api/events/${eventId}/rsvp-page`,
            {
              engine: 'v2',
              v2_template_id: template.templateId,
              v2_content: {},
              v2_theme_overrides: {},
              v2_settings_overrides: {},
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`Template "${template.name}" selected`);
          navigate(`/events/${eventId}/invitation-editor`);
        } catch (e2) {
          toast.error('Failed to create invitation');
        }
      } else {
        toast.error('Failed to select template');
      }
    } finally {
      setSelecting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(`/events/${eventId}`)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Choose a Template</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Select a design to get started</p>
          </div>
        </div>
      </div>

      {/* Template grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Curated templates (DB) */}
        {(loadingCurated || curated.length > 0) && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Curated by Sitoa</h2>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {loadingCurated ? 'Loading…' : `${curated.length} ready-to-edit ${curated.length === 1 ? 'design' : 'designs'}`}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="curated-templates-grid">
              {curated.map((tmpl) => {
                const isSelecting = selecting === tmpl.id;
                const cover = tmpl.cover_image
                  ? (tmpl.cover_image.startsWith('/') ? `${API}${tmpl.cover_image}` : tmpl.cover_image)
                  : null;
                return (
                  <button
                    key={tmpl.id}
                    onClick={() => handleSelectCurated(tmpl)}
                    disabled={!!selecting}
                    className="group text-left rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-all duration-300 hover:shadow-2xl hover:shadow-black/10 dark:hover:shadow-black/30 hover:-translate-y-1 disabled:opacity-60 disabled:hover:translate-y-0"
                    data-testid={`curated-template-card-${tmpl.id}`}
                  >
                    <div className="h-[280px] relative overflow-hidden flex items-center justify-center bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-900/20 dark:to-fuchsia-900/20">
                      {cover ? (
                        <img src={cover} alt={tmpl.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-violet-400">
                          <Sparkles className="w-8 h-8" />
                          <span className="text-[11px] font-medium">No cover yet</span>
                        </div>
                      )}
                      <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-violet-100/90 dark:bg-violet-900/70 text-violet-700 dark:text-violet-200 text-[10px] font-semibold tracking-wider uppercase backdrop-blur-sm">
                        {tmpl.category}
                      </span>
                    </div>
                    <div className="p-4 border-t border-gray-100 dark:border-gray-800">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">{tmpl.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                        {tmpl.description || 'A ready-made design — customize anything.'}
                      </p>
                      {isSelecting && (
                        <div className="mt-2 text-xs text-violet-500 animate-pulse">Applying…</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-center text-[11px] text-gray-400 dark:text-gray-500">
              Not sure? Pick one and customize — you can change everything later.
            </p>
          </div>
        )}

        {/* Built-in (code) templates */}
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Start from a layout</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {NEW_TEMPLATES.map((template) => {
            const pal = palettes[template.theme.palette] || {};
            const isSelecting = selecting === template.templateId;
            return (
              <button
                key={template.templateId}
                onClick={() => handleSelect(template)}
                disabled={!!selecting}
                className="group text-left rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-all duration-300 hover:shadow-2xl hover:shadow-black/10 dark:hover:shadow-black/30 hover:-translate-y-1 disabled:opacity-60 disabled:hover:translate-y-0"
                data-testid={`template-card-${template.templateId}`}
              >
                {/* Live phone preview */}
                <div
                  className="h-[280px] relative overflow-hidden flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${pal.bg || '#f5f5f5'}88, ${pal.surface || pal.bg || '#f5f5f5'}88)` }}
                >
                  {/* Decorative dots */}
                  <div className="absolute top-4 right-4 flex gap-1 z-10">
                    {(template.previewColors || [pal.bg, pal.accent, pal.textPrimary]).filter(Boolean).slice(0, 3).map((c, i) => (
                      <div
                        key={i}
                        className="w-2.5 h-2.5 rounded-full ring-1 ring-white/20"
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  <MiniPhonePreview template={template} />
                </div>

                {/* Info */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{template.name}</h3>
                    <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      {template.sections.length} sections
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">{template.description}</p>
                  {isSelecting && (
                    <div className="mt-2 text-xs text-blue-500 animate-pulse">Setting up...</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
